import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_URL } from '../utils/fatch';
import { useTripInfo } from '../context';
import styles from './sharing.module.css';

/**
 * The page a share link opens. It works logged out: it shows what the trip is
 * and who invited you, then sends you to sign up or log in if you need to.
 */
export function AcceptInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loggedIn, loadingUserInfo, setCurrentTripId } = useTripInfo();

    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState('');

    // Where to land after joining - the email's feature links set this.
    const next = searchParams.get('next') || '/add_expense';

    useEffect(() => {
        const loadInvite = async () => {
            try {
                const response = await fetch(`${API_URL}/invite/${token}`);
                const data = await response.json();

                if (!response.ok) {
                    setLoadError(data.error || 'This invite link is not valid');
                    return;
                }

                setInvite(data);
            } catch (error) {
                console.error('Error loading invite:', error);
                setLoadError('Could not reach the server');
            } finally {
                setLoading(false);
            }
        };

        loadInvite();
    }, [token]);

    const joinTrip = async () => {
        setJoining(true);
        setJoinError('');

        try {
            const response = await fetch(`${API_URL}/sharing/accept`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });

            const data = await response.json();

            if (!response.ok) {
                setJoinError(data.error || 'Could not join the trip');
                return;
            }

            setCurrentTripId(data.tripId);
            navigate(next);
        } catch (error) {
            console.error('Error joining trip:', error);
            setJoinError('Could not reach the server');
        } finally {
            setJoining(false);
        }
    };

    // Signing up or in has to come back here to finish the job.
    const authRedirect = (path) => {
        const returnTo = `/invite/${token}?next=${encodeURIComponent(next)}`;
        navigate(`${path}?returnTo=${encodeURIComponent(returnTo)}&email=${encodeURIComponent(invite?.invitedEmail || '')}`);
    };

    if (loading || loadingUserInfo) {
        return <div className={styles.invitePage}><p className={styles.notice}>Loading your invite…</p></div>;
    }

    if (loadError) {
        return (
            <div className={styles.invitePage}>
                <div className={styles.inviteCard}>
                    <h1>This link doesn&apos;t work</h1>
                    <p className={styles.inviteSub}>{loadError}</p>
                    <Link to="/" className={styles.inviteBtnPrimary}>Go to RoamRite</Link>
                </div>
            </div>
        );
    }

    const dateRange = invite.startDate && invite.endDate
        ? `${new Date(invite.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(invite.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : '';

    return (
        <div className={styles.invitePage}>
            <div className={styles.inviteCard}>
                {invite.image && <div className={styles.inviteImage} style={{ backgroundImage: `url(${invite.image})` }} />}

                <div className={styles.inviteBody}>
                    <p className={styles.inviteFrom}>{invite.inviterName} invited you to a trip</p>
                    <h1 className={styles.inviteTitle}>{invite.tripName}</h1>

                    {(invite.destination || dateRange) && (
                        <p className={styles.inviteMeta}>
                            {invite.destination && <span>📍 {invite.destination}</span>}
                            {invite.destination && dateRange && <span> · </span>}
                            {dateRange && <span>🗓️ {dateRange}</span>}
                        </p>
                    )}

                    {invite.status === 'expired' && (
                        <p className={styles.error}>This invite has expired. Ask {invite.inviterName} to send a new one.</p>
                    )}
                    {invite.status === 'revoked' && (
                        <p className={styles.error}>This invite was revoked.</p>
                    )}

                    {invite.status === 'pending' && (
                        <>
                            <ul className={styles.invitePerks}>
                                <li>💸 Add expenses and see the shared budget</li>
                                <li>🗺️ Plan activities and the itinerary together</li>
                                <li>🎒 Use the AI packing assistant</li>
                            </ul>

                            {loggedIn ? (
                                <>
                                    <button className={styles.inviteBtnPrimary} onClick={joinTrip} disabled={joining}>
                                        {joining ? 'Joining…' : 'Join the trip'}
                                    </button>
                                    {joinError && <p className={styles.error}>{joinError}</p>}
                                </>
                            ) : (
                                <>
                                    <p className={styles.inviteSub}>
                                        This invite is for <strong>{invite.invitedEmail}</strong>. Sign in with that
                                        address to join.
                                    </p>
                                    <button className={styles.inviteBtnPrimary} onClick={() => authRedirect('/signup')}>
                                        Create my account
                                    </button>
                                    <button className={styles.inviteBtnGhost} onClick={() => authRedirect('/login')}>
                                        I already have an account
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
